import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as NumberListIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useGmail } from '../../hooks/useGmail';
import { EmailComposeData, EmailMetadata, EmailDetailData } from '../../types/gmail';

interface EmailComposeProps {
  open: boolean;
  onClose: () => void;
  onSent: () => void;
  replyTo?: EmailMetadata;
  forwardTo?: EmailMetadata;
  forwardToDetail?: EmailDetailData;
  initialTo?: string;
  contactId?: string;
}

export function EmailCompose({
  open,
  onClose,
  onSent,
  replyTo,
  forwardTo,
  forwardToDetail,
  initialTo,
  contactId
}: EmailComposeProps) {
  const { loading, error, sendEmail, clearError } = useGmail();
  
  const [formData, setFormData] = useState<EmailComposeData>({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    isHtml: true,
    threadId: undefined,
    inReplyTo: undefined,
    references: undefined
  });

  const [isHtmlMode] = useState(true);

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open) {
      if (replyTo) {
        // Set up reply
        const emailMatch = replyTo.from.match(/<([^>]+)>/);
        const emailAddress = emailMatch ? emailMatch[1] : replyTo.from;
        
        setFormData({
          to: emailAddress,
          cc: '',
          bcc: '',
          subject: replyTo.subject.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject}`,
          body: '',
          isHtml: true,
          threadId: replyTo.threadId,
          inReplyTo: replyTo.id,
          references: replyTo.id
        });
      } else if (forwardTo) {
        // Set up forward
        const subject = forwardTo.subject.startsWith('Fwd:') || forwardTo.subject.startsWith('Fw:') 
          ? forwardTo.subject 
          : `Fwd: ${forwardTo.subject}`;
        
        // Get email body content - prefer full content from emailDetail, fallback to snippet
        let emailBody = '';
        if (forwardToDetail?.content) {
          // Use HTML if available, otherwise use text
          emailBody = forwardToDetail.content.html || forwardToDetail.content.text || forwardTo.snippet;
        } else {
          emailBody = forwardTo.snippet;
        }
        
        // Format forwarded email body with full content
        const forwardedBody = `\n\n---------- Forwarded message ----------\n` +
          `From: ${forwardTo.from}\n` +
          `Date: ${new Date(forwardTo.date).toLocaleString()}\n` +
          `Subject: ${forwardTo.subject}\n` +
          `To: ${Array.isArray(forwardTo.to) ? forwardTo.to.join(', ') : forwardTo.to || ''}\n\n` +
          emailBody;
        
        setFormData({
          to: '',
          cc: '',
          bcc: '',
          subject,
          body: forwardedBody,
          isHtml: forwardToDetail?.content?.html ? true : false,
          threadId: undefined,
          inReplyTo: undefined,
          references: undefined
        });
      } else if (initialTo) {
        // Set up new email to specific address
        setFormData({
          to: initialTo,
          cc: '',
          bcc: '',
          subject: '',
          body: '',
          isHtml: true,
          threadId: undefined,
          inReplyTo: undefined,
          references: undefined
        });
      } else {
        // New email
        setFormData({
          to: '',
          cc: '',
          bcc: '',
          subject: '',
          body: '',
          isHtml: true,
          threadId: undefined,
          inReplyTo: undefined,
          references: undefined
        });
      }
    }
  }, [open, replyTo, forwardTo, forwardToDetail, initialTo]);

  const handleInputChange = (field: keyof EmailComposeData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSend = async () => {
    if (!formData.to || !formData.subject) {
      return;
    }

    clearError();
    const success = await sendEmail(formData, contactId);
    
    if (success) {
      onSent();
      onClose();
    }
  };

  const handleClose = () => {
    clearError();
    onClose();
  };

  const insertTextAtCursor = (text: string) => {
    const textarea = document.getElementById('email-body') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentText = formData.body;
      const newText = currentText.substring(0, start) + text + currentText.substring(end);
      
      setFormData(prev => ({
        ...prev,
        body: newText
      }));
      
      // Restore cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + text.length, start + text.length);
      }, 0);
    }
  };

  const toggleBold = () => {
    if (isHtmlMode) {
      insertTextAtCursor('<b></b>');
    } else {
      insertTextAtCursor('**');
    }
  };

  const toggleItalic = () => {
    if (isHtmlMode) {
      insertTextAtCursor('<i></i>');
    } else {
      insertTextAtCursor('*');
    }
  };

  const toggleUnderline = () => {
    if (isHtmlMode) {
      insertTextAtCursor('<u></u>');
    }
  };

  const toggleBulletList = () => {
    if (isHtmlMode) {
      insertTextAtCursor('<ul>\n<li></li>\n</ul>');
    } else {
      insertTextAtCursor('\n- ');
    }
  };

  const toggleNumberList = () => {
    if (isHtmlMode) {
      insertTextAtCursor('<ol>\n<li></li>\n</ol>');
    } else {
      insertTextAtCursor('\n1. ');
    }
  };

  const insertLink = () => {
    if (isHtmlMode) {
      insertTextAtCursor('<a href=""></a>');
    } else {
      insertTextAtCursor('[text](url)');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {replyTo ? 'Reply to Email' : 'Compose Email'}
          </Typography>
          
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Recipients */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              label="To"
              value={formData.to}
              onChange={handleInputChange('to')}
              fullWidth
              required
              size="small"
            />
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="CC"
                value={formData.cc}
                onChange={handleInputChange('cc')}
                size="small"
                sx={{ flexGrow: 1 }}
              />
              <TextField
                label="BCC"
                value={formData.bcc}
                onChange={handleInputChange('bcc')}
                size="small"
                sx={{ flexGrow: 1 }}
              />
            </Box>
          </Box>

          <Divider />

          {/* Subject */}
          <TextField
            label="Subject"
            value={formData.subject}
            onChange={handleInputChange('subject')}
            fullWidth
            required
            size="small"
          />

          <Divider />

          {/* Formatting Toolbar */}
          <Paper sx={{ p: 1, bgcolor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                Format:
              </Typography>
              
              <IconButton size="small" onClick={toggleBold} title="Bold">
                <BoldIcon fontSize="small" />
              </IconButton>
              
              <IconButton size="small" onClick={toggleItalic} title="Italic">
                <ItalicIcon fontSize="small" />
              </IconButton>
              
              <IconButton size="small" onClick={toggleUnderline} title="Underline">
                <UnderlineIcon fontSize="small" />
              </IconButton>
              
              <Divider orientation="vertical" flexItem />
              
              <IconButton size="small" onClick={toggleBulletList} title="Bullet List">
                <BulletListIcon fontSize="small" />
              </IconButton>
              
              <IconButton size="small" onClick={toggleNumberList} title="Numbered List">
                <NumberListIcon fontSize="small" />
              </IconButton>
              
              <IconButton size="small" onClick={insertLink} title="Insert Link">
                <LinkIcon fontSize="small" />
              </IconButton>
              
              <Divider orientation="vertical" flexItem />
              
              <IconButton size="small" title="Attach File">
                <AttachIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>

          {/* Email Body */}
          <TextField
            id="email-body"
            label="Message"
            value={formData.body}
            onChange={handleInputChange('body')}
            fullWidth
            multiline
            rows={12}
            placeholder={isHtmlMode ? "Type your message here... (HTML supported)" : "Type your message here..."}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: isHtmlMode ? 'inherit' : 'monospace'
              }
            }}
          />

          {/* Mode Indicator */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Chip
              label={isHtmlMode ? 'HTML Mode' : 'Plain Text'}
              size="small"
              color={isHtmlMode ? 'primary' : 'default'}
            />
            
            <Typography variant="caption" color="text.secondary">
              {formData.body.length} characters
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleSend}
          disabled={loading || !formData.to || !formData.subject}
        >
          {loading ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
